import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import EmptyState from '@/components/base/EmptyState';
import SectionHeading from '@/components/base/SectionHeading';
import { allNews, formatDate } from '@/lib/catalog';
import { newsCategories } from '@/mocks/news';
import { specials } from '@/mocks/specials';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';

const PAGE_SIZE = 5;

export default function NewsPage() {
  const [category, setCategory] = useState('全部资讯');
  const [page, setPage] = useState(1);

  useDocumentMeta({
    title: '午夜电影网 - 最新电影资讯',
    description:
      '午夜电影网资讯频道，提供影评、业内动态、奖项观察、幕后故事与主题片单，帮你看懂银幕之外正在发生的事。',
    keywords: '影视资讯,电影新闻,影评,电影幕后,电影奖项,午夜电影网',
  });

  const filtered = useMemo(
    () => (category === '全部资讯' ? allNews : allNews.filter((item) => item.category === category)),
    [category],
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const visible = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const changeCategory = (value: string) => {
    setCategory(value);
    setPage(1);
  };

  return (
    <div>
      <section className="relative overflow-hidden border-b border-background-200 bg-accent-100 pb-10 pt-24 md:pb-14 md:pt-32">
        <div className="absolute -left-20 bottom-0 h-64 w-64 rounded-full bg-primary-500/10 blur-3xl" />
        <div className="shell relative">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '电影资讯' }]} />
          <div className="mt-5">
            <p className="eyebrow text-[11px] text-primary-500">Film Journal</p>
            <h1 className="mt-2 font-heading text-3xl font-semibold tracking-wide text-foreground-950 md:text-5xl">
              最新电影资讯
            </h1>
            <p className="mt-3 max-w-2xl text-sm leading-7 text-foreground-500">
              不只告诉你上映了什么，也告诉你它为什么值得看。影评、幕后、奖项与行业观察，每周持续更新。
            </p>
          </div>
        </div>
      </section>

      <div className="shell py-10 md:py-14">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
          <div className="min-w-0 lg:col-span-8">
            <div className="hide-scrollbar -mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
              {newsCategories.map((item) => {
                const active = category === item;
                return (
                  <button
                    key={item}
                    type="button"
                    onClick={() => changeCategory(item)}
                    aria-pressed={active}
                    className={`whitespace-nowrap rounded-full px-4 py-2 text-xs transition-colors ${
                      active
                        ? 'bg-primary-500 font-medium text-background-50'
                        : 'border border-background-300/70 text-foreground-600 hover:border-primary-500/60 hover:text-foreground-900'
                    }`}
                  >
                    {item}
                  </button>
                );
              })}
            </div>

            <div className="mt-6 space-y-4">
              {visible.length === 0 ? (
                <EmptyState
                  icon="ri-newspaper-line"
                  title="暂无最新资讯"
                  description="这个分类下暂时还没有内容，换个分类看看，或者回到首页浏览推荐。"
                  actionLabel="查看全部资讯"
                  actionTo="/news"
                />
              ) : (
                visible.map((item) => (
                  <Link
                    key={item.id}
                    to={`/news/${item.id}`}
                    className="group grid grid-cols-1 gap-4 rounded-lg border border-background-200 bg-background-100 p-3 transition-colors hover:border-primary-500/40 sm:grid-cols-[220px_1fr] md:p-4"
                  >
                    <div className="h-44 w-full overflow-hidden rounded-md bg-background-200 sm:h-36">
                      <img
                        src={item.cover}
                        alt={item.title}
                        title={`${item.title} - 影视资讯封面`}
                        className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                      />
                    </div>
                    <div className="flex min-w-0 flex-col justify-between gap-3">
                      <div>
                        <div className="flex flex-wrap items-center gap-2 text-[11px] text-foreground-500">
                          <span className="rounded bg-primary-500/10 px-2 py-0.5 text-primary-500">{item.category}</span>
                          <span>{formatDate(item.date)}</span>
                          <span className="h-1 w-1 rounded-full bg-foreground-500/60" />
                          <span>{item.author}</span>
                        </div>
                        <h2 className="mt-2 line-clamp-2 text-base leading-7 text-foreground-900 transition-colors group-hover:text-primary-500 md:text-lg md:leading-8">
                          {item.title}
                        </h2>
                        <p className="mt-2 line-clamp-2 text-xs leading-6 text-foreground-500 md:text-sm">{item.excerpt}</p>
                      </div>
                      <div className="flex items-center justify-between text-[11px]">
                        <span className="line-clamp-1 text-foreground-500">{item.subtitle}</span>
                        <span className="flex shrink-0 items-center gap-1 whitespace-nowrap text-primary-500">
                          阅读全文
                          <i className="ri-arrow-right-line" aria-hidden="true"></i>
                        </span>
                      </div>
                    </div>
                  </Link>
                ))
              )}
            </div>

            {visible.length > 0 && totalPages > 1 && (
              <div className="mt-8 flex flex-wrap items-center justify-center gap-2">
                <button
                  type="button"
                  onClick={() => setPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300/70 text-foreground-600 transition-colors hover:border-primary-500/70 hover:text-primary-500 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <i className="ri-arrow-left-s-line" aria-hidden="true"></i>
                </button>
                {Array.from({ length: totalPages }, (_, index) => index + 1).map((pageNumber) => (
                  <button
                    key={pageNumber}
                    type="button"
                    onClick={() => setPage(pageNumber)}
                    aria-current={pageNumber === currentPage ? 'page' : undefined}
                    className={`h-9 w-9 rounded-md text-xs transition-colors ${
                      pageNumber === currentPage
                        ? 'bg-primary-500 font-medium text-background-50'
                        : 'border border-background-300/70 text-foreground-600 hover:border-primary-500/70 hover:text-primary-500'
                    }`}
                  >
                    {pageNumber}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300/70 text-foreground-600 transition-colors hover:border-primary-500/70 hover:text-primary-500 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
                </button>
              </div>
            )}
          </div>

          <aside className="space-y-4 lg:col-span-4">
            <div className="rounded-lg border border-background-200 bg-background-100 p-5">
              <h2 className="flex items-center gap-2 font-heading text-xl tracking-wide text-foreground-950">
                <i className="ri-fire-line text-primary-500" aria-hidden="true"></i>
                热门资讯
              </h2>
              <ol className="mt-4 space-y-4">
                {allNews.slice(0, 4).map((item, index) => (
                  <li key={item.id}>
                    <Link to={`/news/${item.id}`} className="group flex items-start gap-3">
                      <span className="font-heading text-lg leading-none text-primary-500/80">
                        {String(index + 1).padStart(2, '0')}
                      </span>
                      <span className="min-w-0">
                        <span className="line-clamp-2 block text-xs leading-6 text-foreground-700 transition-colors group-hover:text-primary-500">
                          {item.title}
                        </span>
                        <span className="mt-1 block text-[10px] text-foreground-500">{formatDate(item.date)}</span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ol>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-100 p-5">
              <h2 className="flex items-center gap-2 font-heading text-xl tracking-wide text-foreground-950">
                <i className="ri-layout-grid-line text-primary-500" aria-hidden="true"></i>
                专题推荐
              </h2>
              <div className="mt-4 space-y-3">
                {specials.slice(0, 2).map((item) => (
                  <Link key={item.id} to={`/specials/${item.id}`} className="group flex gap-3">
                    <span className="h-14 w-20 shrink-0 overflow-hidden rounded bg-background-200">
                      <img
                        src={item.cover}
                        alt={item.title}
                        className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </span>
                    <span className="min-w-0">
                      <span className="line-clamp-1 block text-xs text-foreground-800 transition-colors group-hover:text-primary-500">
                        {item.title}
                      </span>
                      <span className="mt-1 block text-[10px] text-foreground-500">共 {item.movieIds.length} 部影片</span>
                    </span>
                  </Link>
                ))}
              </div>
              <Link
                to="/specials"
                className="mt-4 flex items-center justify-center gap-1 rounded-md border border-background-300/70 py-2 text-[11px] text-foreground-600 transition-colors hover:border-primary-500/70 hover:text-primary-500"
              >
                全部专题
                <i className="ri-arrow-right-line" aria-hidden="true"></i>
              </Link>
            </div>
          </aside>
        </div>

        <div className="mt-14">
          <SectionHeading
            eyebrow="Keep exploring"
            title="想直接看片？"
            description="资讯看累了，就去片库按类型和评分挑一部，今晚总有一部适合你。"
            actionLabel="进入电影片库"
            actionTo="/movies"
          />
        </div>
      </div>
    </div>
  );
}